import { ref } from "vue";
import { AppConstants } from "@/constants/AppConstants";
import { AxiosError } from "axios";
import messages from "@/assets/messages.json";
import apiClientAuth from "@/util/apiClientAuth";
interface CategoryItem {
  categoryId: string;
  categoryName: string;
}

const items = ref<CategoryItem[]>(getItemsFromLocalStorage());
const errorMessage = ref<string | null>(null);

function getItemsFromLocalStorage(): CategoryItem[] {
  const storedItems = localStorage.getItem(
    AppConstants.SITE_SERCH_CATEGORY_SESSION_NAME
  );
  return storedItems ? JSON.parse(storedItems) : [];
}

function saveItemsToLocalStorage(items: CategoryItem[]): void {
  localStorage.setItem(
    AppConstants.SITE_SERCH_CATEGORY_SESSION_NAME,
    JSON.stringify(items)
  );
}

export function useSiteSerchCategoryStore() {
  function addItem(item: CategoryItem) {
    items.value.push(item);
    saveItemsToLocalStorage(items.value);
  }

  function removeItem(categoryId: string) {
    items.value = items.value.filter((item) => item.categoryId !== categoryId);
    saveItemsToLocalStorage(items.value);
  }

  function updateItem(updatedItem: CategoryItem) {
    const index = items.value.findIndex(
      (item) => item.categoryId === updatedItem.categoryId
    );
    if (index !== -1) {
      items.value[index] = updatedItem;
      saveItemsToLocalStorage(items.value);
    }
  }

  function getItems(): CategoryItem[] {
    return items.value;
  }

  /**
   * サイト内検索カテゴリリスト取得APIからサイト内検索のカテゴリ項目を取得し、ストアを更新します。
   * @param {string} agcode -AGコード
   * @param {string} userId -ユーザーID
   * @returns {Promise<string | null>} エラーが発生した場合はエラーメッセージを含む文字列、正常に取得できた場合は null を返す Promise。
   */
  async function fetchSiteSerchCagetoryFromAPI(
    agcode: string,
    userId: string
  ): Promise<string | null> {
    try {
      const response = await apiClientAuth.post(
        "/api/SiteSearch/getCategoryList",
        {
          systemKbn: AppConstants.SYSTEM_KBN_WITH,
          agCode: agcode,
          userId: userId,
        }
      );
      items.value = response.data;
      saveItemsToLocalStorage(items.value);

      // 取得したデータでストアの項目を更新する
      items.value = response.data.categoryListData;

      // 取得した項目をローカルストレージに保存する
      saveItemsToLocalStorage(items.value);

      // 前回のエラーメッセージをクリアする
      errorMessage.value = null;

      // エラーが発生しなかったことを示すために null を返す
      return null;
    } catch (error) {
      const axiosError = error as AxiosError;
      if (axiosError.response) {
        switch (axiosError.response.status) {
          case 401:
            errorMessage.value = messages.ERR_USER002;
            break;
          case 500:
            errorMessage.value = messages.ERR_SYS999;
            break;
          default:
            errorMessage.value = messages.ERR_SYS001;
        }
      } else if (axiosError.request) {
        errorMessage.value = messages.ERR_SYS001;
      } else {
        errorMessage.value = messages.ERR_SYS002;
        if (process.env.VUE_APP_DEBUG === "true") {
          console.log("Error: ", axiosError.message);
        }
      }

      return errorMessage.value;
    }
  }

  return {
    items,
    addItem,
    removeItem,
    updateItem,
    getItems,
    fetchSiteSerchCagetoryFromAPI,
    errorMessage,
  };
}
